import React, { useMemo, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { useUserApplication } from '../../../context/UserApplicationContext';
import { ArrowRight, FileText, CheckCircle, Clock, AlertTriangle, Plus, MessageSquare, Shield, ExternalLink } from 'lucide-react';
import { Button } from '../../../common';
import RecentActivity from './RecentActivity';
import {
  getStatusLabel,
  isQuoteStage,
  isPaidApplication,
  isCompletedApplication,
  MARKETING_WIZARD_URL,
} from '../../../utils/applicationStatus';

// Ken's hosted photo — same hotlinked asset the email signatures use
// (see backend/api/shared/services/senders.js)
const KEN_PHOTO_URL = 'https://www.markotm.com/assets/images/ken-mcinnes.png';

const DashboardHome = ({ onNavigate }) => {
  const { user } = useUser();
  const { t } = useTranslation('dashboard');
  const { currentApplication, applications = [] } = useUserApplication();
  const [kenPhotoFailed, setKenPhotoFailed] = useState(false);

  // Calculate stats using real data.
  // Quote-stage leads are NOT "Active Filings" — a filing only becomes active once paid.
  const stats = useMemo(() => {
    const active = applications.filter(app =>
      isPaidApplication(app) && !isCompletedApplication(app) && app.status !== 'cancelled'
    ).length;
    const completed = applications.filter(app => isCompletedApplication(app)).length;

    // "Action Required": a confirmed quote awaiting payment, explicit action states,
    // or a pending payment.
    const actionRequired = applications.filter(app => {
      if (app.status === 'quote_sent' && !isPaidApplication(app)) return true;
      if (app.status === 'action_required' || app.status === 'rejected' || app.status === 'payment_pending') {
        return true;
      }
      return false;
    }).length;

    return { active, completed, actionRequired };
  }, [applications]);

  // Progress for the current application — status-driven (quote-first funnel)
  const currentAppProgress = useMemo(() => {
    if (!currentApplication) return 0;
    const status = currentApplication.status;

    if (isCompletedApplication(currentApplication)) return 100;
    if (status === 'filed') return 90;
    if (status === 'filing_in_progress') return 80;
    if (isPaidApplication(currentApplication)) return 70;
    if (status === 'quote_sent') return 50;
    if (status === 'quote_requested') return 35;
    // Legacy/active applications that predate the quote flow
    if (currentApplication.pricingId) return 50;
    if (currentApplication.analysisId || currentApplication.analysisCompleted) return 30;
    return 15;
  }, [currentApplication]);

  // Generate dynamic Marko Insights — status-aware for the quote-first funnel
  const insights = useMemo(() => {
    const items = [];

    if (currentApplication) {
      const status = currentApplication.status;

      if (isPaidApplication(currentApplication) && !isCompletedApplication(currentApplication)) {
        items.push({
          type: 'success',
          title: t('home.insights.filingInProgress', 'Filing In Progress'),
          message: t('home.insights.filingInProgressMsg', 'Your payment is complete. Your trademark application is being prepared for filing.'),
          action: () => onNavigate('payments')
        });
      } else if (status === 'quote_requested') {
        items.push({
          type: 'info',
          title: t('home.insights.reportPreparing', 'Report On Its Way'),
          message: t('home.insights.reportPreparingMsg', 'We are preparing your trademark report and quote. Expect an email within 24 hours.'),
          action: () => onNavigate('payments')
        });
      } else if (status === 'quote_sent') {
        items.push({
          type: 'warning',
          title: t('home.insights.quoteReady', 'Your Quote Is Ready'),
          message: t('home.insights.quoteReadyMsg', 'Your attorney-confirmed quote is ready. Review it and pay to start your filing.'),
          action: () => onNavigate('payments')
        });
      } else if (status === 'payment_pending') {
        items.push({
          type: 'warning',
          title: t('home.insights.paymentPending', 'Payment Pending'),
          message: t('home.insights.paymentPendingMsg', 'Your application is ready for filing. Complete payment to proceed.'),
          action: () => onNavigate('payments')
        });
      } else if (!isCompletedApplication(currentApplication) && status !== 'cancelled') {
        items.push({
          type: 'info',
          title: t('home.insights.appInProgress', 'Application in Progress'),
          message: t('home.insights.appInProgressMsg', 'Check your quote and payment status to move your application forward.'),
          action: () => onNavigate('payments')
        });
      }
    } else {
      items.push({
        type: 'info',
        title: t('home.insights.startJourney', 'Start Your Journey'),
        message: t('home.insights.startJourneyMsg', 'Get a free trademark check and quote on our website — no payment required upfront.'),
        action: () => { window.location.href = MARKETING_WIZARD_URL; }
      });
    }

    // Analysis insight — only when relevant and not already covered above
    if (currentApplication && (currentApplication.analysisId || currentApplication.analysisCompleted)) {
      const alreadyShownFilingProgress = isPaidApplication(currentApplication);
      if (!alreadyShownFilingProgress && !isQuoteStage(currentApplication.status)) {
        items.push({
          type: 'success',
          title: t('home.insights.analysisComplete', 'Analysis Completed'),
          message: t('home.insights.analysisCompleteMsg', 'Marko has analysed your trademark. Check the report for potential conflicts.'),
          action: () => onNavigate('payments')
        });
      }
    }

    // Dynamic status based on app count
    const totalApps = applications?.length || 0;
    const completedApps = applications?.filter(isCompletedApplication)?.length || 0;

    if (completedApps > 0) {
      items.push({
        type: 'success',
        title: `${completedApps} Trademark${completedApps > 1 ? 's' : ''} Protected`,
        message: `You have ${completedApps} registered trademark${completedApps > 1 ? 's' : ''} in your portfolio.`,
        action: () => onNavigate('portfolio')
      });
    } else if (totalApps === 0) {
      items.push({
        type: 'system',
        title: t('home.insights.systemOp', 'Ready to Help'),
        message: 'Marko is ready to guide you through the trademark process.',
      });
    } else {
      items.push({
        type: 'system',
        title: t('home.insights.systemOp', 'System Operational'),
        message: t('home.insights.systemOpMsg', 'All monitoring systems active'),
      });
    }

    return items;
  }, [currentApplication, applications, onNavigate, t]);

  // Continue-CTA label for the current application card
  const continueLabel = useMemo(() => {
    if (!currentApplication) return t('home.continueFiling', 'View Quote & Payment');
    if (currentApplication.status === 'quote_sent' && !isPaidApplication(currentApplication)) {
      return t('home.payAndFile', 'Pay & File');
    }
    if (currentApplication.status === 'quote_requested') {
      return t('home.viewQuoteStatus', 'View Status');
    }
    if (isPaidApplication(currentApplication)) {
      return t('home.viewFilingStatus', 'View Filing Status');
    }
    return t('home.continueFiling', 'View Quote & Payment');
  }, [currentApplication, t]);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
            {t('home.welcome', 'Welcome back, {{name}}', { name: user?.firstName || t('home.guest', 'Guest') })}
          </h1>
          <p className="text-gray-600 mt-2 text-base md:text-lg">
            {t('home.subtitle', "Here's what's happening with your intellectual property today.")}
          </p>
        </div>
        {/* New filings start on the marketing site (quote-first funnel) */}
        <Button
          onClick={() => { window.location.href = MARKETING_WIZARD_URL; }}
          className="w-full md:w-auto justify-center flex items-center gap-2 shadow-lg hover:shadow-xl transition-all bg-[#004d40] hover:bg-[#00695c] text-white px-6 py-3 rounded-xl"
        >
          <Plus className="h-5 w-5" />
          {t('home.newFiling', 'New Trademark Check')}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatsCard
          title={t('home.activeFilings', 'Active Filings')}
          value={stats.active}
          icon={FileText}
          color="blue"
          onClick={() => onNavigate('portfolio')}
        />
        <StatsCard
          title={t('home.actionRequired', 'Action Required')}
          value={stats.actionRequired}
          icon={AlertTriangle}
          color="amber"
          onClick={() => onNavigate('payments')}
        />
        <StatsCard
          title={t('home.protectedAssets', 'Protected Assets')}
          value={stats.completed}
          icon={CheckCircle}
          color="green"
          onClick={() => onNavigate('portfolio')}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Action Zone (Current App + Quick Actions) */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">{t('home.currentApplication')}</h2>
            {currentApplication && (
              <button
                onClick={() => onNavigate('portfolio')}
                className="text-sm font-medium text-[#00695c] hover:text-[#004d40] flex items-center gap-1"
              >
                {t('home.viewAll', 'View All')} <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>

          {currentApplication ? (
            <div
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-all cursor-pointer group relative overflow-hidden"
              onClick={() => onNavigate('payments')}
            >
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <FileText className="h-32 w-32 text-gray-400 transform rotate-12" />
              </div>

              <div className="relative z-10">
                <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
                  <div className="flex items-center gap-3 md:gap-4 min-w-0">
                    <div className="h-12 w-12 md:h-14 md:w-14 rounded-2xl bg-[#e0f2f1] flex items-center justify-center text-[#00695c] flex-shrink-0">
                      <FileText className="h-6 w-6 md:h-7 md:w-7" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 group-hover:text-[#00695c] transition-colors break-words">
                        {currentApplication.trademarkName || 'New Trademark Application'}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Application ID: {currentApplication.id?.slice(0, 8).toUpperCase()}
                      </p>
                    </div>
                  </div>
                  <span className={`px-4 py-1.5 rounded-full text-sm font-medium border whitespace-nowrap flex-shrink-0 ${
                    isCompletedApplication(currentApplication)
                      ? 'bg-green-50 text-green-700 border-green-100'
                      : currentApplication.status === 'quote_sent'
                        ? 'bg-amber-50 text-amber-700 border-amber-100'
                        : 'bg-blue-50 text-blue-700 border-blue-100'
                  }`}>
                    {getStatusLabel(currentApplication.status, t)}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">{t('home.applicationProgress')}</span>
                    <span className="font-medium text-gray-900">
                      {currentAppProgress}%
                    </span>
                  </div>
                  <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#00695c] rounded-full transition-all duration-1000"
                      style={{ width: `${currentAppProgress}%` }}
                    ></div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="h-4 w-4" />
                    <span>{t('home.lastUpdated', 'Last updated: {{date}}', { date: new Date(currentApplication.updatedAt || Date.now()).toLocaleDateString() })}</span>
                  </div>
                  <div className="flex items-center gap-1 text-[#00695c] font-bold text-sm group-hover:gap-2 transition-all">
                    {continueLabel} <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-10 shadow-sm border border-gray-200 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#004d40] to-[#00796b]"></div>
              <div className="mx-auto h-20 w-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                <Plus className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('home.noActiveApplications')}</h3>
              <p className="text-gray-500 mb-8 max-w-md mx-auto leading-relaxed">
                {t('home.startProtecting', 'Start with a free trademark check on our website. You will get a detailed report and an attorney-confirmed quote within 24 hours — no payment needed upfront.')}
              </p>
              <Button
                onClick={() => { window.location.href = MARKETING_WIZARD_URL; }}
                className="px-8 py-3 text-lg rounded-xl shadow-lg bg-[#004d40] hover:bg-[#00695c] inline-flex items-center gap-2"
              >
                {t('home.startNewApp', 'Start Your Free Check')}
                <ExternalLink className="h-5 w-5" />
              </Button>
            </div>
          )}

          {/* Quick Links - Moved to Left Column */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
            <h3 className="font-bold text-gray-900 mb-4">{t('home.quickActions')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button onClick={() => onNavigate('payments')} className="text-left p-4 rounded-xl bg-gray-50 hover:bg-[#e0f2f1] border border-transparent hover:border-[#b2dfdb] transition-all group">
                <div className="bg-white w-10 h-10 rounded-lg flex items-center justify-center mb-3 shadow-sm group-hover:scale-110 transition-transform">
                  <FileText className="h-5 w-5 text-gray-600 group-hover:text-[#00695c]" />
                </div>
                <span className="font-medium text-gray-900 block mb-1">{t('home.resumeFiling')}</span>
                <span className="text-xs text-gray-500">{t('home.resumeFilingDesc')}</span>
              </button>

              <button onClick={() => onNavigate('settings')} className="text-left p-4 rounded-xl bg-gray-50 hover:bg-[#e0f2f1] border border-transparent hover:border-[#b2dfdb] transition-all group">
                <div className="bg-white w-10 h-10 rounded-lg flex items-center justify-center mb-3 shadow-sm group-hover:scale-110 transition-transform">
                  <FileText className="h-5 w-5 text-gray-600 group-hover:text-[#00695c]" />
                </div>
                <span className="font-medium text-gray-900 block mb-1">{t('home.paymentHistory')}</span>
                <span className="text-xs text-gray-500">{t('home.paymentHistoryDesc')}</span>
              </button>

              <button onClick={() => onNavigate('messages')} className="text-left p-4 rounded-xl bg-gray-50 hover:bg-[#e0f2f1] border border-transparent hover:border-[#b2dfdb] transition-all group">
                <div className="bg-white w-10 h-10 rounded-lg flex items-center justify-center mb-3 shadow-sm group-hover:scale-110 transition-transform">
                  <MessageSquare className="h-5 w-5 text-gray-600 group-hover:text-[#00695c]" />
                </div>
                <span className="font-medium text-gray-900 block mb-1">{t('home.contactSupport')}</span>
                <span className="text-xs text-gray-500">{t('home.contactSupportDesc')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - Information Zone (Insights + Activity) */}
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-5 w-5 text-[#00695c]" />
            {t('home.fromYourAttorney', 'From Your Attorney')}
          </h2>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 relative overflow-hidden tutorial-marko-chat">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#004d40] to-[#00796b]"></div>

            <div className="flex items-start gap-4 mb-4">
              <div className="relative">
                {kenPhotoFailed ? (
                  <div className="h-12 w-12 rounded-full bg-[#e0f2f1] border-2 border-white shadow-md flex items-center justify-center text-sm font-bold text-[#00695c]">
                    KM
                  </div>
                ) : (
                  <img
                    src={KEN_PHOTO_URL}
                    alt={t('attorney.name', 'Ken McInnes')}
                    onError={() => setKenPhotoFailed(true)}
                    className="h-12 w-12 rounded-full object-cover border-2 border-white shadow-md"
                  />
                )}
                <span className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 border-2 border-white rounded-full"></span>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">{t('attorney.name', 'Ken McInnes')}</p>
                <p className="text-xs text-gray-500">{t('attorney.title', 'Head of Trademark Strategy')}</p>
              </div>
            </div>

            <div className="bg-teal-50 rounded-xl p-4 mb-4 border border-teal-100">
              <p className="text-gray-800 text-sm leading-relaxed">
                {t('attorney.greeting', "Welcome to your secure IP dashboard. You can follow your trade mark's progress here, and it makes it easy to stay in touch with your assigned trade mark attorney at every step.")}
              </p>
            </div>

            <div className="space-y-3">
              {insights.map((insight, index) => (
                <div
                  key={index}
                  onClick={insight.action}
                  className={`flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100 ${insight.action ? 'cursor-pointer' : ''}`}
                >
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    insight.type === 'success' ? 'bg-green-50 text-green-600' :
                    insight.type === 'warning' ? 'bg-amber-50 text-amber-600' :
                    insight.type === 'info' ? 'bg-blue-50 text-blue-600' :
                    'bg-[#e0f2f1] text-[#00695c]'
                  }`}>
                    {insight.type === 'success' ? <CheckCircle className="h-4 w-4" /> :
                     insight.type === 'warning' ? <AlertTriangle className="h-4 w-4" /> :
                     insight.type === 'info' ? <Clock className="h-4 w-4" /> :
                     <Shield className="h-4 w-4" />}
                  </div>
                  <div className="text-sm">
                    <p className="font-medium text-gray-900">{insight.title}</p>
                    <p className="text-gray-500 text-xs mt-0.5">{insight.message}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity - Moved to Right Column */}
          <RecentActivity currentApplication={currentApplication} />
        </div>
      </div>
    </div>
  );
};

const StatsCard = ({ title, value, icon: Icon, color }) => {
  const colorStyles = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 flex items-center gap-4 hover:shadow-md transition-shadow">
      <div className={`h-14 w-14 rounded-2xl flex items-center justify-center ${colorStyles[color]} transition-transform hover:scale-110`}>
        <Icon className="h-7 w-7" />
      </div>
      <div>
        <p className="text-gray-500 text-sm font-medium mb-1">{title}</p>
        <p className="text-3xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );
};

export default DashboardHome;
